import { useEffect, useRef } from "react";
import cx from "classnames";
import { axisLeft } from "d3-axis";
import { select } from "d3-selection";
import "d3-transition"; // will attach itself to `select`

import "./y-axis.scss";

import { dataTypeFormatter, DATA_TYPES } from "../../common/format.js";

import { bool, func, number, oneOf, shape, string } from "prop-types";

const propTypes = {
  className: string,
  dataType: oneOf(DATA_TYPES),
  scale: func.isRequired,
  width: number.isRequired,
  height: number.isRequired,
  margin: shape({
    left: number,
    top: number,
  }),
  tickSize: number,
  tickPadding: number,
  transitionDuration: number,
  gridLines: bool,
};

const defaultProps = {
  margin: {},
  transitionDuration: 250,
  gridLines: false,
  tickPadding: 10, // default for EGH
  tickSize: 6, // d3 default https://github.com/d3/d3-axis#axis_tickSize
};

function YAxis(props) {
  const {
    className,
    dataType,
    scale,
    width,
    margin,
    height,
    tickPadding,
    tickSize,
    transitionDuration,
    gridLines,
    ...otherProps
  } = props;

  const yAxisRef = useRef();

  useEffect(() => {
    const yAxis = axisLeft(scale)
      .tickSize(gridLines ? width * -1 : tickSize)
      .ticks(Math.round(height / 125)) // one tick every 125px
      .tickPadding(tickPadding)
      .tickFormat(dataTypeFormatter(dataType));

    select(yAxisRef.current)
      .transition()
      .duration(transitionDuration)
      .call(yAxis);

    const ticks = select(yAxisRef.current).selectAll("g.tick");

    // adjust the tick text
    ticks.selectAll("text").attr("dx", "-0.75em"); // add padding between the text and the chart

    // hide the y-axis line
    select(yAxisRef.current).select("path").style("display", "none");
  }, [
    dataType,
    gridLines,
    height,
    scale,
    tickPadding,
    tickSize,
    transitionDuration,
    width,
  ]);

  return (
    <g
      className={cx("y-axis", className, {
        "y-axis--grid-lines": gridLines,
      })}
      ref={yAxisRef}
      transform={`translate(${margin.left || 0}, ${margin.top || 0})`}
      {...otherProps}
    />
  );
}

YAxis.propTypes = propTypes;
YAxis.defaultProps = defaultProps;
export default YAxis;
